import React from "react";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { MdRepeat, MdAutorenew } from "react-icons/md";
import { useOrderHistory } from "../../hooks/useOrderHistory";
import styles from "./OrderHistoryItem.module.scss";
import { OrderInterface } from "../../../../../common/types/types";
import { useTheme } from "../../../../../common/hooks/useTheme";
import { DeliveryService } from "../../../../../common/types/enums";
import {
  getDeliveryTypeIcon,
  getPaymentMethodIcon,
} from "../../../../../common/utils/functions/enumToIcon";
import {
  getDeliveryStatusText,
  getDeliveryTypeText,
  getPaymentMethodText,
} from "../../../../../common/utils/functions/enumToText";

interface OrderHistoryItemProps {
  order: OrderInterface;
  onPress: (orderId: number) => void;
  onRepeat?: (order: OrderInterface) => void;
  isRepeatDisabled?: (order: OrderInterface) => boolean;
}

const OrderHistoryItem: React.FC<OrderHistoryItemProps> = ({
  order,
  onPress,
  onRepeat,
  isRepeatDisabled,
}) => {
  const { getOrderStatusInfo } = useOrderHistory();
  const { theme } = useTheme();

  const handlePress = () => onPress(Number(order.id));
  const handleRepeat = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRepeat?.(order);
  };

  const repeatDisabled = isRepeatDisabled ? isRepeatDisabled(order) : true;

  const DeliveryIcon = getDeliveryTypeIcon(order.deliveryType);
  const PaymentIcon = getPaymentMethodIcon(order.paymentMethod);
  const statusInfo = getOrderStatusInfo(order.status);
  const StatusIcon = statusInfo.icon;

  const hasActiveTransition =
    order?.transitions && order.transitions.length > 0;
  const transitionEndTime =
    hasActiveTransition && order?.transitions
      ? order.transitions[order.transitions.length - 1].executeAt
      : null;

  const cdekDelivery =
    order.deliveryService === DeliveryService.CDEK && order.delivery?.length
      ? order.delivery[0]
      : null;

  return (
    <div
      className={styles.orderCard}
      onClick={handlePress}
      style={{ backgroundColor: theme.card, borderColor: theme.border }}
    >
      <div className={styles.orderHeader}>
        <span className={styles.orderId} style={{ color: theme.text }}>
          Заказ #{order.id}
        </span>
        {order.createdAt && (
          <span
            className={styles.orderDate}
            style={{ color: theme.textSecondary }}
          >
            {format(new Date(order.createdAt), "dd.MM.yyyy HH:mm", {
              locale: ru,
            })}
          </span>
        )}
      </div>

      <div
        className={styles.orderStatus}
        style={{ backgroundColor: `${statusInfo.backgroundColor}` }}
      >
        <StatusIcon
          size={16}
          color={statusInfo.textColor}
          className={styles.statusIcon}
        />
        <span
          className={styles.statusText}
          style={{ color: statusInfo.textColor }}
        >
          {statusInfo.text}
        </span>
      </div>

      {hasActiveTransition && transitionEndTime && (
        <div className={styles.transitionBadge}>
          <MdAutorenew size={14} color="var(--primary)" />
          <span className={styles.transitionText}>
            Автосмена • завершится в{" "}
            {format(new Date(transitionEndTime), "HH:mm")}
          </span>
        </div>
      )}

      {cdekDelivery && (
        <div className={styles.cdekInfo}>
          <div className={styles.cdekRow}>
            <span className={styles.cdekLabel}>СДЭК</span>
            <span className={styles.cdekStatus}>
              {getDeliveryStatusText(cdekDelivery.status) || "Обрабатывается"}
            </span>
          </div>
          {cdekDelivery.pickupPointName && (
            <div className={styles.cdekRow}>
              <span className={styles.cdekLabel}>ПВЗ:</span>
              <span className={styles.cdekValue}>
                {cdekDelivery.pickupPointName}
              </span>
            </div>
          )}
          {cdekDelivery.deliveryOrderId && (
            <a
              href={`https://www.cdek.ru/ru/tracking?order_id=${cdekDelivery.deliveryOrderId}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.cdekLink}
              onClick={(e) => e.stopPropagation()}
            >
              Отследить
            </a>
          )}
        </div>
      )}

      <div className={styles.orderInfo}>
        <div className={styles.infoRow}>
          <div className={styles.infoIcon}>
            <DeliveryIcon size={16} color={theme.primary} />
          </div>
          <span className={styles.infoText} style={{ color: theme.text }}>
            {getDeliveryTypeText(order.deliveryType)}
          </span>
        </div>
        <div className={styles.infoRow}>
          <div className={styles.infoIcon}>
            <PaymentIcon size={16} color={theme.primary} />
          </div>
          <span className={styles.infoText} style={{ color: theme.text }}>
            {getPaymentMethodText(order.paymentMethod)} •{" "}
            {order.isPaid ? "Оплачен" : "Не оплачен"}
          </span>
        </div>
      </div>

      {order.items && order.items.length > 0 && (
        <div className={styles.orderItems}>
          <span
            className={styles.itemsTitle}
            style={{ color: theme.textSecondary }}
          >
            Состав заказа:
          </span>
          {order.items.slice(0, 2).map((orderItem: any, index: number) => (
            <div key={index} className={styles.itemRow}>
              <span className={styles.itemName} style={{ color: theme.text }}>
                {orderItem.name}
              </span>
              <div className={styles.itemDetails}>
                <span
                  className={styles.itemQuantity}
                  style={{ color: theme.textSecondary }}
                >
                  {orderItem.quantity} ×
                </span>
                <span
                  className={styles.itemPrice}
                  style={{ color: theme.text }}
                >
                  {Number(orderItem.total).toLocaleString("ru-RU")} ₽
                </span>
              </div>
            </div>
          ))}
          {order.items.length > 2 && (
            <span className={styles.moreItems} style={{ color: theme.primary }}>
              +{order.items.length - 2} еще...
            </span>
          )}
        </div>
      )}

      <div
        className={styles.orderFooter}
        style={{ borderTopColor: theme.border }}
      >
        <div className={styles.totalInfo}>
          <span
            className={styles.totalLabel}
            style={{ color: theme.textSecondary }}
          >
            Итого:
          </span>
          <span className={styles.totalAmount} style={{ color: theme.primary }}>
            {Number(order.total).toLocaleString("ru-RU")} ₽
          </span>
        </div>
        {onRepeat && !repeatDisabled && (
          <button className={styles.repeatButton} onClick={handleRepeat}>
            <MdRepeat size={18} color="#fff" />
            <span className={styles.repeatText}>Повторить</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default OrderHistoryItem;
